import {Time} from 'yougile-util'
import {
  SandboxTree,
  SprintStickerState,
  StickerStateId,
  StickerType,
  Tag
} from 'yougile-model'
import {Sticker, StickerColor} from '../sticker'
import {ACustomStickerCtl} from './a-custom-sticker-ctl'

export class SprintCtl extends ACustomStickerCtl<SprintStickerState> {
  get type(): StickerType {
    return 'sprint'
  }

  private sortStates(sticker: Tag): void {
    const {index} = sticker.states
    const all = Object.keys(index).map(k => index[k] as SprintStickerState)
    const now = Time.now()
    all.sort((a: SprintStickerState, b: SprintStickerState) => {
      const noA = !a.begin || !a.end
      const noB = !b.begin || !b.end
      if (noA && noB) {
        return a.name > b.name ? 1 : -1
      } else if (noA) {
        return 1
      } else if (noB) {
        return -1
      } else {
        const pA = Time.placementDay(now, a.begin, a.end)
        const pB = Time.placementDay(now, b.begin, b.end)
        if (pA === pB) {
          return a.end < b.end ? 1 : -1
        } else {
          return pA > pB ? 1 : -1
        }
      }
    })
    sticker.states.order = all.map(s => s.id)
    sticker.commit()
  }

  override addState(sticker: Tag, state: SprintStickerState): void {
    sticker.states.index[state.id] = state
    this.sortStates(sticker)
  }

  override changeState(
    sticker: Tag,
    id: StickerStateId,
    state: SprintStickerState
  ): void {
    super.changeState(sticker, id, state)
    this.sortStates(sticker)
  }

  override make(box: SandboxTree): Tag {
    const sticker = super.make(box)
    this.addState(sticker, {id: this.makeShortId(), name: 'Backlog'})
    this.addState(sticker, {
      id: this.makeShortId(),
      name: 'Sprint 1',
      begin: Time.now(),
      end: Time.now() + 14 * 24 * 3600 * 1000
    })
    return sticker
  }

  makeNewState(): SprintStickerState {
    return {
      id: this.makeShortId(),
      name: ''
    }
  }

  static formatInterval(state: SprintStickerState): string | undefined {
    return state.begin && state.end
      ? `(${Time.formatDate(state.begin)} - ${Time.formatDate(state.end)})`
      : undefined
  }

  static formatName(state: SprintStickerState): string {
    return state.name
  }

  static formatSprint(state: SprintStickerState): string {
    const {formatInterval, formatName} = SprintCtl
    const interval = formatInterval(state)

    return `${formatName(state)}${interval ? ` ${interval}` : ''}`
  }

  static color(state: SprintStickerState): StickerColor {
    const now = Date.now()
    const {textColor, defaultStateColor, borderColor, backgroundColor} = Sticker
    const {begin, end} = state

    if (now >= begin && now < end) {
      const main = '#7c5d19'

      return {
        main,
        border: '#ffe09a',
        background: '#fffaef',
        text: textColor(main)
      }
    }

    if (begin >= now) {
      const main = '#13587b'

      return {
        main,
        border: '#b3d9ff',
        background: '#e4f2ff',
        text: textColor(main)
      }
    }

    const main = defaultStateColor()

    return {
      main,
      border: borderColor(main),
      background: backgroundColor(main),
      text: textColor(main)
    }
  }
}
